import { apiFetch } from "./client";

/** Fixed lead pipeline phases  — mirror of the backend
 * LeadStage enum, in exact pipeline order. */
export type LeadStage =
  | "NOV"
  | "POKUSAN_KONTAKT"
  | "LOS_TAJMING"
  | "NEKVALIFIKOVAN"
  | "U_OBRADI"
  | "KVALIFIKOVAN"
  | "PRILIKA"
  | "DISKVALIFIKOVAN"
  | "DOBIJEN";

/** Colour/behaviour class of a stage — the offers OPEN/WON/LOST analogue. */
export type LeadStageKind = "ACTIVE" | "WON" | "LOST";

/** Lead "temperature" — mirror of the backend enum. */
export type LeadClassification = "TOPLO" | "MLAKO" | "HLADNO";

/** The three classifications, warmest → coldest — drives the form/filter option order. */
export const LEAD_CLASSIFICATIONS: LeadClassification[] = ["TOPLO", "MLAKO", "HLADNO"];

export type Lead = {
  id: string;
  companyId: string;
  /** The prospect FIRM's name (used as the company name on convert). */
  name: string;
  /** Contact person at the firm; their email/phone are the fields below. */
  contactPerson: string | null;
  /** The contact person's position/title at the firm (e.g. "Direktor nabavke"). */
  contactPosition: string | null;
  email: string | null;
  phone: string | null;
  /** ISO-3166-1 alpha-2 country code of the firm; carried to the company on convert. */
  country: string | null;
  /** The firm's address; carried to the company's address on convert. */
  addressLine1?: string | null;
  postalCode?: string | null;
  city?: string | null;
  classification: LeadClassification | null;
  source: string | null;
  value: number | null;
  currency: string;
  ownerId: string;
  ownerName: string | null;
  stage: LeadStage;
  stageKind: LeadStageKind;
  note: string | null;
  /** Why the lead was lost; set only while it sits in a LOST-kind stage. */
  lossReason: string | null;
  convertedCompanyId: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
};

export type LeadCreate = {
  name: string;
  contactPerson?: string | null;
  contactPosition?: string | null;
  email?: string | null;
  phone?: string | null;
  country?: string | null;
  classification?: LeadClassification | null;
  source?: string | null;
  value?: number | null;
  currency?: string | null;
  ownerId?: string | null;
  /** Optional initial pipeline phase; defaults to NOV server-side when omitted. */
  stage?: LeadStage | null;
  addressLine1?: string | null;
  postalCode?: string | null;
  city?: string | null;
  note?: string | null;
};

export type LeadUpdate = {
  name?: string | null;
  contactPerson?: string | null;
  email?: string | null;
  phone?: string | null;
  country?: string | null;
  classification?: LeadClassification | null;
  source?: string | null;
  value?: number | null;
  currency?: string | null;
  ownerId?: string | null;
  addressLine1?: string | null;
  postalCode?: string | null;
  city?: string | null;
  note?: string | null;
};

export type ConvertResponse = { companyId: string };

/**
 * What the ERP requires for a customer: tax number, registration number, country and customer group (the
 * group of the ERP customer tree). All four are mandatory; the new customer goes to the ERP at once.
 */
export type ConvertRequest = {
  vatNumber: string;
  registrationNumber: string;
  country: string;
  erpGroup: string;
  addressLine1?: string;
  postalCode?: string;
  city?: string;
};

/** A customer group of the ERP: the name the customer goes to the ERP with, and its path in the ERP's tree. */
export type ErpGroupOption = {
  value: string;
  label: string;
};

export const leadsApi = {
  list: () => apiFetch<Lead[]>("/leads"),
  get: (id: string) => apiFetch<Lead>(`/leads/${id}`),
  create: (body: LeadCreate) => apiFetch<Lead>("/leads", { method: "POST", body }),
  update: (id: string, body: LeadUpdate) => apiFetch<Lead>(`/leads/${id}`, { method: "PUT", body }),
  /** Free movement between stages; a LOST-kind target requires lossReason. */
  changeStage: (id: string, stage: LeadStage, lossReason?: string) =>
    apiFetch<Lead>(`/leads/${id}/stage`, { method: "PATCH", body: { stage, lossReason } }),
  /** Allowed only from DOBIJEN; idempotent (already-converted returns the existing companyId). */
  convert: (id: string, body: ConvertRequest) =>
    apiFetch<ConvertResponse>(`/leads/${id}/convert`, { method: "POST", body }),
  /** The ERP's customer groups in the order of its tree, each with its path there. */
  erpGroups: () => apiFetch<ErpGroupOption[]>("/leads/erp-groups"),
  remove: (id: string) => apiFetch<void>(`/leads/${id}`, { method: "DELETE" }),
};
